import { useContext, useMemo, useState, useEffect } from "react";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { parseCsv } from "@/utils/csv";

type Step5ConfirmationProps = {
  selectedTemplateId: string;
  uploadedFile: File | null;
  onCsvDataParsed: (
    data: Array<{
      contact: string;
      name: string;
      crmId?: string;
      templateVariables: Record<string, string>;
    }>
  ) => void;
  broadcastName: string;
  onBroadcastNameChange: (name: string) => void;
  enableAiAgent: boolean;
  // Real send count after recipient classification; null while unknown
  effectiveRecipientCount?: number | null;
};

type ParsedCSVData = {
  headers: string[];
  rows: Record<string, string>[];
};

const MAX_BROADCAST_RECIPIENTS = 1000;

/**
 * The uploaded contact for a row: the `contact` column, else the legacy `phone`
 * one. Both are accepted so every CSV a merchant already has keeps working —
 * the backend takes the same pair and normalizes to `contact`.
 */
const contactOf = (row: Record<string, string>): string =>
  row.contact?.trim() || row.phone?.trim() || "";

const Step5Confirmation = ({
  selectedTemplateId,
  uploadedFile,
  onCsvDataParsed,
  broadcastName,
  onBroadcastNameChange,
  enableAiAgent,
  effectiveRecipientCount,
}: Step5ConfirmationProps) => {
  const { merchantId } = useContext(MerchantContext);
  const [csvData, setCsvData] = useState<ParsedCSVData | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const { data, isLoading } = trpc.whatsappTemplates.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  // Find the selected template
  const selectedTemplate = useMemo(() => {
    if (!data?.templates || !selectedTemplateId) return null;
    return data.templates.find((t) => t.id === selectedTemplateId);
  }, [data?.templates, selectedTemplateId]);

  // Extract parameters from template body text
  const templateParameters = useMemo(() => {
    if (!selectedTemplate?.bodyPreview) return [];

    const matches = selectedTemplate.bodyPreview.matchAll(/{{(\w+)}}/g);
    const params = Array.from(matches).map((match) => match[1]);

    // Remove duplicates
    return [...new Set(params)];
  }, [selectedTemplate]);

  // Parse CSV file
  useEffect(() => {
    if (!uploadedFile) return;

    const parseCSV = async () => {
      setIsParsing(true);
      setParseError(null);

      try {
        const text = await uploadedFile.text();
        const { headers, rows: parsedRows } = parseCsv(text);

        if (parsedRows.length === 0) {
          setParseError("El archivo CSV debe contener al menos una fila de datos.");
          return;
        }

        // Validate headers - name is required, but don't duplicate if it's also in template params
        const templateParamsWithoutNameAndCrmId = templateParameters.filter((p) => p !== "name" && p !== "crmId");
        const requiredHeaders = ["name", ...templateParamsWithoutNameAndCrmId];
        const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

        // Either column satisfies the contact requirement, so a file exported
        // before the column was renamed still uploads.
        if (!headers.includes("contact") && !headers.includes("phone")) {
          missingHeaders.unshift("contact");
        }

        if (missingHeaders.length > 0) {
          setParseError(`Faltan columnas requeridas en el CSV: ${missingHeaders.join(", ")}`);
          return;
        }

        const rows = parsedRows.map((values) => {
          const row: Record<string, string> = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          return row;
        });

        // Filter out empty rows (must have a contact and a name). Trimmed,
        // because a whitespace-only cell passes a length check but normalizes to
        // "" — and an empty contact is not a recipient.
        const validRows = rows.filter((row) => contactOf(row) && row.name?.trim());

        if (validRows.length === 0) {
          setParseError("No se encontraron filas válidas en el archivo CSV.");
          return;
        }

        if (validRows.length > MAX_BROADCAST_RECIPIENTS) {
          setParseError(
            `El archivo contiene ${validRows.length} destinatarios y el máximo permitido es ${MAX_BROADCAST_RECIPIENTS}. Divide el archivo en envíos más pequeños e intenta de nuevo.`
          );
          return;
        }

        setCsvData({ headers, rows: validRows });

        // Convert to format expected by backend
        const recipients = validRows.map((row) => {
          // Both contact columns are pulled out, so neither leaks into the
          // template variables when a file carries the legacy header.
          const { contact, phone, name, crmId, ...templateVariables } = row;
          const finalTemplateVars = { ...templateVariables };
          if (templateParameters.includes("name")) {
            finalTemplateVars.name = name;
          }
          if (templateParameters.includes("crmId") && crmId) {
            finalTemplateVars.crmId = crmId;
          }
          return {
            // Re-read through the helper rather than the destructured pair: a
            // file with no `phone` column at all leaves that binding undefined.
            contact: contactOf(row),
            name,
            ...(crmId && crmId.length > 0 ? { crmId } : {}),
            templateVariables: finalTemplateVars,
          };
        });

        // Pass parsed data to parent
        onCsvDataParsed(recipients);
      } catch (error) {
        setParseError("Error al leer el archivo CSV. Por favor verifica el formato.");
      } finally {
        setIsParsing(false);
      }
    };

    parseCSV();
  }, [uploadedFile, templateParameters]);

  // Generate preview message from first row
  const previewMessage = useMemo(() => {
    if (!selectedTemplate?.bodyPreview || !csvData || csvData.rows.length === 0) {
      return null;
    }

    const firstRow = csvData.rows[0];
    let message = selectedTemplate.bodyPreview;

    // Replace all parameters with values from first row
    templateParameters.forEach((param) => {
      const value = firstRow[param] || `[${param}]`;
      message = message.replace(new RegExp(`{{${param}}}`, "g"), value);
    });

    return message;
  }, [selectedTemplate, csvData, templateParameters]);

  if (isLoading || isParsing) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="xl" />
          <p className="text-gray-600">{isParsing ? "Procesando archivo..." : "Cargando..."}</p>
        </div>
      </div>
    );
  }

  if (!selectedTemplate) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="py-8 text-center text-gray-500">
            No se pudo cargar la plantilla seleccionada.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (parseError) {
    return (
      <div className="flex flex-col gap-4">
        <Alert status="error">
          <p>{parseError}</p>
        </Alert>
      </div>
    );
  }

  if (!csvData) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="py-8 text-center text-gray-500">
            No se pudo procesar el archivo CSV.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-lg font-bold">Confirmar Envío</p>
        <p className="mb-4 text-sm text-gray-600">
          Revisa la información antes de enviar los mensajes.
        </p>
      </div>

      {/* Broadcast Name Input */}
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">
          Nombre del envío masivo <span className="text-red-500">*</span>
        </Label>
        <Input
          placeholder="Ej: Campaña Enero 2026"
          value={broadcastName}
          onChange={(e) => onBroadcastNameChange(e.target.value)}
        />
      </div>

      {/* Summary Card */}
      <Card className="border-2 border-purple-200 bg-purple-50">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="flex flex-col items-center gap-1">
              <p className="text-3xl font-bold text-purple-600">
                {effectiveRecipientCount ?? csvData.rows.length}
              </p>
              <p className="text-sm text-gray-600">Mensajes a enviar</p>
              {effectiveRecipientCount != null &&
                effectiveRecipientCount < csvData.rows.length && (
                  <p className="text-xs text-gray-500">
                    de {csvData.rows.length} destinatarios en el archivo
                  </p>
                )}
            </div>
            <div className="flex flex-col items-center gap-1">
              <p className="text-lg font-bold text-purple-600">
                {selectedTemplate.name}
              </p>
              <p className="text-sm text-gray-600">Plantilla seleccionada</p>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Badge
                variant="outline"
                className={
                  enableAiAgent ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                }
              >
                {enableAiAgent ? "Activado" : "Desactivado"}
              </Badge>
              <p className="text-sm text-gray-600">Agente IA</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            <div>
              <div className="mb-3 flex items-center gap-2">
                <p className="font-bold">Vista Previa del Mensaje</p>
                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">Ejemplo con primer lead</Badge>
              </div>
              <p className="mb-3 text-sm text-gray-500">
                Así se verá el mensaje para: <strong>{csvData.rows[0].name}</strong> (
                {contactOf(csvData.rows[0])})
              </p>
            </div>

            <Separator />

            {/* Message Preview */}
            <Card className="border border-green-200 bg-green-50">
              <CardContent className="p-4">
                <p className="whitespace-pre-wrap text-sm text-gray-800">
                  {previewMessage}
                </p>
              </CardContent>
            </Card>

            {/* Parameters Used */}
            {templateParameters.length > 0 && (
              <div>
                <p className="mb-2 text-xs text-gray-500">
                  Parámetros utilizados:
                </p>
                <div className="flex flex-wrap gap-2">
                  {templateParameters.map((param) => (
                    <Badge key={param} variant="outline" className="bg-purple-100 text-purple-800 border-purple-200 text-xs">
                      {param}: {csvData.rows[0][param]}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Warning */}
      <Alert status="warning">
        <p className="text-sm">
          Al hacer clic en "Enviar Mensajes", se enviarán{" "}
          <strong>{effectiveRecipientCount ?? csvData.rows.length}</strong> mensajes de WhatsApp.
          Esta acción no se puede deshacer.
        </p>
      </Alert>
    </div>
  );
};

export default Step5Confirmation;
